/**
 * Academy PEOPLE directory (F3-D): Students / Teachers / Staff / Guardians
 * tabs from the admin streams (org/campus). Search by name/national ID,
 * campus filter, and invite with role+campus. All local-first; editing the
 * record lives on the detail route (link per row).
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Search, UserPlus, Users } from "@fleet/icons";
import { buildDirectory, type DirectoryRow, hydrateDirectory, type PeopleTab } from "@space/shared";
import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Avatar } from "@/components/ui/avatar";
import { PageContainer, PageHeader, Skeleton } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { EmptyState } from "@/components/ui/empty-state";
import { PillTabs } from "@/components/ui/pill-tabs";
import { listEntranceProps, useListEntrance } from "@/lib/animation/list-entrance";
import { ExportButton } from "@/features/io/components/export-button";
import { useActiveOrg } from "../../hooks/use-active-org";
import { useCampuses } from "../../hooks/use-academy-data";
import { useMyOrgRole, useOrgDirectory } from "../../hooks/use-people-data";
import { useOrgIdentities } from "../../hooks/use-org-identities";
import { PeopleInviteDialog } from "./people-invite-dialog";

const TABS: { id: PeopleTab; label: MessageDescriptor; inviteRole: string }[] = [
  { id: "students", label: msg`Students`, inviteRole: "student" },
  { id: "teachers", label: msg`Teachers`, inviteRole: "teacher" },
  { id: "staff", label: msg`Staff`, inviteRole: "staff" },
  { id: "guardians", label: msg`Guardians`, inviteRole: "guardian" },
];

export const PeopleDirectoryView: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const campuses = useCampuses(activeOrgId);
  const directory = useOrgDirectory(activeOrgId);
  // D3: identity (name/image/email) arrives via the single source. Email
  // only comes through if the server decides this actor is entitled to it.
  const people = useOrgIdentities(activeOrgId);
  const rbac = useMyOrgRole(activeOrgId);

  const [tab, setTab] = createSignal<PeopleTab>("students");
  const [search, setSearch] = createSignal("");
  const [campusId, setCampusId] = createSignal<string>("");
  const [inviteOpen, setInviteOpen] = createSignal(false);

  const partitioned = createMemo(() =>
    buildDirectory(hydrateDirectory(directory.data ?? [], people.identities()), {
      search: search(),
      campusId: campusId() || null,
    }),
  );

  const rows = () => partitioned()[tab()];
  const countFor = (id: PeopleTab) => partitioned()[id].length;
  const tabItems = createMemo(() => TABS.map((td) => ({ id: td.id, label: t(td.label) })));

  // The tab is part of the scope: switching from Students to Teachers is a
  // list change (user action), not data arriving. PowerSync refetches don't
  // touch it → the entrance doesn't replay.
  const isEntering = useListEntrance({
    scope: () => `people:${activeOrgId() ?? ""}:${tab()}`,
    hasItems: () => rows().length > 0,
  });

  const campusOptions = () => [
    { value: "", label: t(msg`All campuses`) },
    ...(campuses.data ?? []).map((c) => ({ value: c.id, label: c.name ?? c.id })),
  ];

  const canInvite = () => rbac.can("ManageMembers");
  const inviteRoleForTab = () => TABS.find((x) => x.id === tab())?.inviteRole ?? "student";

  return (
    <PageContainer maxWidth="4xl">
      <div class="space-y-5">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <PageHeader
            title={t(msg`People`)}
            description={t(msg`Students, teachers, staff and guardians of your academy.`)}
            icon={Users}
          />
          <div class="flex items-center gap-2">
            {/* F4-B: export of the student directory (io.students) with the campus filter. */}
            <ExportButton
              entityKey="io.students"
              orgId={activeOrgId()}
              filters={{ campus_id: campusId() || undefined }}
            />
            <Show when={canInvite()}>
              <Button
                class="gap-2"
                data-testid="people-invite-open"
                onClick={() => setInviteOpen(true)}
              >
                <UserPlus class="h-4 w-4" /> {t(msg`Invite`)}
              </Button>
            </Show>
          </div>
        </div>

        {/* Filters: search + campus */}
        <div class="flex flex-wrap items-center gap-3">
          <div class="relative min-w-[220px] flex-1">
            <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              data-testid="people-search"
              value={search()}
              onInput={(e) => setSearch(e.currentTarget.value)}
              placeholder={t(msg`Search by name or ID…`)}
              class="w-full rounded-xl border-2 border-border bg-field-background py-2 pl-10 pr-3 text-sm focus-visible:border-primary focus-visible:outline-none"
            />
          </div>
          <div class="w-52">
            <Select
              options={campusOptions()}
              value={campusId()}
              onChange={setCampusId}
              aria-label={t(msg`Filter by campus`)}
            />
          </div>
        </div>

        {/* Tabs */}
        <PillTabs
          tabs={tabItems()}
          value={tab()}
          onChange={setTab}
          testIdPrefix="people-tab"
          label={t(msg`People categories`)}
          trailing={(id) => (
            <span class="rounded-full bg-accent/12 px-1.5 text-xs font-bold text-accent">
              {countFor(id)}
            </span>
          )}
        />

        {/* List */}
        <Show
          when={!directory.isLoading}
          fallback={
            <div class="space-y-2">
              <Skeleton class="h-16 w-full rounded-xl" />
              <Skeleton class="h-16 w-full rounded-xl" />
              <Skeleton class="h-16 w-full rounded-xl" />
            </div>
          }
        >
          <Show
            when={rows().length > 0}
            fallback={
              <EmptyState
                icon={Users}
                title={t(msg`No people here yet`)}
                description={t(msg`Invite members or adjust your search and campus filters.`)}
              />
            }
          >
            <ul class="space-y-2" data-testid="people-list">
              <For each={rows()}>
                {(row, index) => <PersonRow row={row} entering={isEntering()} index={index()} />}
              </For>
            </ul>
          </Show>
        </Show>
      </div>

      <Show when={activeOrgId()}>
        {(orgId) => (
          <PeopleInviteDialog
            open={inviteOpen()}
            onOpenChange={setInviteOpen}
            organizationId={orgId()}
            campuses={campuses.data ?? []}
            defaultRole={inviteRoleForTab()}
            onInvited={() => void directory.refetch?.()}
          />
        )}
      </Show>
    </PageContainer>
  );
};

const PersonRow: Component<{ row: DirectoryRow; entering: boolean; index: number }> = (props) => {
  const { t } = useI18n();
  const entrance = () => listEntranceProps(props.entering, props.index);
  return (
    <li
      data-testid="person-row"
      data-user-id={props.row.userId ?? ""}
      class={entrance().class}
      style={entrance().style}
    >
      <Link
        to="/academy/people/$userId"
        params={{ userId: props.row.userId ?? "" }}
        class="flex items-center justify-between gap-3 rounded-xl border border-border p-3 transition-[color,background-color,border-color,transform] duration-150 ease-out hover:border-primary/50 hover:bg-muted/30 active:scale-[0.99]"
      >
        <div class="flex min-w-0 items-center gap-3">
          {/* `image` arrives redacted (null) for those who can't show their
              photo to third parties — rule zero, applied server-side. */}
          <Avatar
            userId={props.row.userId}
            name={props.row.name}
            email={props.row.email}
            image={props.row.image}
            testId="person-row-avatar"
          />
          <div class="min-w-0">
            <p class="truncate text-sm font-semibold">
              {props.row.name ?? props.row.email ?? props.row.userId}
            </p>
            <p class="truncate text-xs text-muted-foreground">
              <Show when={props.row.nationalId}>{(id) => <>{id()} · </>}</Show>
              {props.row.email}
            </p>
          </div>
        </div>
        <span class="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">
          {props.row.role ?? ""}
        </span>
      </Link>
      {/* AUTONOMY: teachers have their own public profile editor —
          before, it was unreachable from the directory (direct URL only). */}
      <Show when={props.row.role === "teacher" && props.row.userId}>
        {(uid) => (
          <Link
            to="/academy/teachers/$userId"
            params={{ userId: uid() }}
            class="mt-1 inline-flex items-center gap-1 pl-3 text-xs font-medium text-primary hover:underline"
            data-testid={`teacher-editor-link-${uid()}`}
          >
            {t(msg`Public teacher page →`)}
          </Link>
        )}
      </Show>
    </li>
  );
};
